import { IScreenData } from "@features/shared/entities/onboarding";
import { onboardingStarted, userQueryCollected } from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { parseQueryParams } from "@features/helpers";
import { Language } from "i18n";

export const setOnboardingDataById = createEvent<string>();
export const setOnboardingLang = createEvent<Language>();
export const clearOnboardingSession = createEvent();

const $onboardingId = createStore<string>("").reset(clearOnboardingSession);
const $data = createStore<IScreenData[]>([]).reset(clearOnboardingSession);
const $totalSteps = createStore(0).reset(clearOnboardingSession);
const $totalStepsWithoutPaywall = createStore(0).reset(clearOnboardingSession);
const $lang = createStore<Language>(Language.EN).reset(clearOnboardingSession);

const onboardingLoaders: Record<string, () => Promise<IScreenData[]>> = {
    challenge_v1_our: () => import("@data/onboardings/v1/screens").then((m) => m.onboardingV1Data),
    product_v1: () =>
        import("@data/onboardings/productOnboarding/productOnboarding").then((m) => m.productOnboardingData),
    product_v2: () =>
        import("@data/onboardings/productOnboarding/productOnboardingV2").then((m) => m.productOnboardingDataV2),
    product_v3: () =>
        import("@data/onboardings/productOnboarding/productOnboardingV3").then((m) => m.productOnboardingDataV3),
    product_v4: () =>
        import("@data/onboardings/productOnboarding/productOnboardingV4").then((m) => m.productOnboardingDataV4),
    challenge_v2_our: () => import("@data/onboardings/v2/screens").then((m) => m.onboardingV2Data),
    challenge_100k: () => import("@data/onboardings/100k/screens").then((m) => m.data100KChallenge),
    challenge_100k_v2: () => import("@data/onboardings/100k/screens").then((m) => m.data100KChallenge),
    challenge_100k_v3: () => import("@data/onboardings/100k _v3/screens").then((m) => m.data100KChallengeV3),
    challenge_100k_v4: () => import("@data/onboardings/100k _v4/screens").then((m) => m.data100KChallengeV4),
    challenge_100k_v5: () => import("@data/onboardings/100k _v5/screens").then((m) => m.data100KChallengeV5),
    challenge_100k_v6: () => import("@data/onboardings/100k _v6/screens").then((m) => m.data100KChallengeV6),
    challenge_100k_v7: () => import("@data/onboardings/100k_v7/screens").then((m) => m.data100KChallengeV7),
    challenge_100k_v7_ecommpay: () => import("@data/onboardings/100k_v7/screens").then((m) => m.data100KChallengeV7),
    challenge_100k_v8: () => import("@data/onboardings/100k_v8/screens").then((m) => m.data100KChallengeV8),
    challenge_100k_v8_s: () => import("@data/onboardings/100k_v8/screens").then((m) => m.data100KChallengeV8),
    challenge_apple: () => import("@data/onboardings/100k_apple/screens").then((m) => m.data100KAppleChallenge),
    challenge_apple_v2: () =>
        import("@data/onboardings/challenge_apple_v2/screens").then((m) => m.dataChallengeAppleV2),
    challenge_100k_v8_e: () => import("@data/onboardings/100k_v8/screens").then((m) => m.data100KChallengeV8),
    challenge_100k_v8_es: () => import("@data/onboardings/100k_v8_es/screens").then((m) => m.data100KChallengeV8ES),
    challenge_100k_v8_1: () => import("@data/onboardings/100k_v8_1/screens").then((m) => m.data100KChallengeV81),
    email_onboarding: () => import("@data/onboardings/email_onboarding/screens").then((m) => m.dataEmailOnboarding),
    challenge_100k_v8_2: () => import("@data/onboardings/100k_v8/screens").then((m) => m.data100KChallengeV8),
    challenge_100k_v8_3: () => import("@data/onboardings/100k_v8/screens").then((m) => m.data100KChallengeV8),
    challenge_100k_v8_4: () => import("@data/onboardings/100k_v8/screens").then((m) => m.data100KChallengeV8),
    challenge_100k_v9: () => import("@data/onboardings/100k_v9/screens").then((m) => m.data100KChallengeV9),
};

const loadScreensFx = createEffect(async (id: string): Promise<IScreenData[]> => {
    const loader = onboardingLoaders[id];
    if (!loader) return [];
    try {
        return await loader();
    } catch (e) {
        console.error("[onboarding] failed to load screens", id, e);
        return [];
    }
});

const trackOnboardingStartedFx = createEffect<{ onboarding: string }, void>(({ onboarding }) => {
    onboardingStarted(onboarding);
});

const saveOnboardingIdFx = createEffect((id: string) => {
    if (typeof window !== "undefined") {
        const pathname = window.location.pathname;
        const shouldSave = pathname.startsWith("/experiment") || pathname.startsWith("/onboarding");
        if (shouldSave) {
            sessionStorage.setItem("onboarding_id", id);
        }
    }
});

const saveTotalStepsFx = createEffect((totalSteps: number) => {
    if (
        typeof window !== "undefined" &&
        (window.location.pathname.startsWith("/experiment") || window.location.pathname.startsWith("/onboarding"))
    ) {
        sessionStorage.setItem("onboarding_total_steps", `${totalSteps}`);
    }
});

const saveQueryParamsFx = createEffect(() => {
    if (typeof window === "undefined") return;

    const query = window.location.search;
    if (!query) return;

    const prevSent = sessionStorage.getItem("query_params_sent");
    sessionStorage.setItem("query_params", query);

    if (prevSent === query) return;

    sessionStorage.setItem("query_params_sent", query);
    const parsed = parseQueryParams(query);
    userQueryCollected(parsed);
});

const clearSessionStorageFx = createEffect(() => {
    if (typeof window !== "undefined") {
        sessionStorage.removeItem("onboarding_total_steps");
        sessionStorage.removeItem("onboarding_answers");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("onboarding_id");
        sessionStorage.removeItem("query_params");
        sessionStorage.removeItem("query_params_sent");
        sessionStorage.removeItem("had_inactive_subscription");
    }
});

sample({
    clock: loadScreensFx.doneData,
    target: $data
});

sample({
    clock: loadScreensFx.doneData,
    filter: () => {
        return window.location.pathname.startsWith("/experiment") || window.location.pathname.startsWith("/onboarding");
    },
    target: saveQueryParamsFx
});

sample({
    clock: $data,
    fn: (state) => state.length - 1,
    target: $totalSteps
});

sample({
    clock: $data,
    fn: (screens) => screens.filter((screen) => screen.screenType !== "PaywallScreen").length - 1,
    target: $totalStepsWithoutPaywall
});

sample({
    clock: setOnboardingDataById,
    source: { currentId: $onboardingId, currentData: $data },
    filter: ({ currentId, currentData }, newId) => {
        return currentId !== newId || currentData.length === 0;
    },
    fn: (_, newId) => newId,
    target: [$onboardingId, loadScreensFx]
});

sample({
    clock: $onboardingId,
    filter: (id): id is string => !!id,
    target: saveOnboardingIdFx
});

sample({
    clock: $totalSteps,
    filter: (step): step is number => !!step,
    target: saveTotalStepsFx
});

sample({
    clock: clearOnboardingSession,
    target: clearSessionStorageFx
});

sample({
    clock: setOnboardingLang,
    target: $lang
});

const contract = new ModelContract(
    {
        $data,
        $totalSteps,
        $totalStepsWithoutPaywall,
        $onboardingId,
        $lang
    },
    {
        setOnboardingDataById,
        setOnboardingLang,
        clearOnboardingSession
    },
    { trackOnboardingStartedFx },
    {}
);

export type IOnboardingContract = typeof contract;

export class OnboardingModule extends ModuleDescription<IOnboardingContract> {
    getDefaultContract() {
        return contract;
    }

    run(): void { }
}
